<template>
    <div
        class="slice-content-columns-duo-column"
        :class="{
            [`slice-content-columns-duo-column--align-${align}`]: true,
        }"
        :data-mui-theme="theme"
        :data-testid="`slice-content-columns-duo-column--${index}`"
    >
        <MuiTag v-if="isFilled.keyText(tag)" revert :data-testid="`slice-content-columns-duo-tag--${index}`">
            {{ tag }}
        </MuiTag>

        <PrismicRichText
            v-if="isFilled.richText(title)"
            :field="title"
            class="slice-content-columns-duo-column__title"
            :data-testid="`slice-content-columns-duo-title--${index}`"
        />

        <PrismicRichText
            v-if="isFilled.richText(description)"
            :field="description"
            class="slice-content-columns-duo-column__description"
            :data-testid="`slice-content-columns-duo-description--${index}`"
        />

        <div
            v-if="visibleImages.length"
            class="slice-content-columns-duo-column__duo mui-background"
            :data-testid="`slice-content-columns-duo-well--${index}`"
        >
            <PrismicImage
                v-for="(item, imageIndex) in visibleImages"
                :key="imageIndex"
                :field="item.image"
                :alt="item.image.alt ?? undefined"
                loading="lazy"
                sizes="(max-width: 767px) 90vw, 40vw"
                class="slice-content-columns-duo-column__image"
                :data-testid="`slice-content-columns-duo-image--${index}-${imageIndex}`"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import type {ImageField, KeyTextField, RichTextField} from '@prismicio/types';
    import {PrismicImage, PrismicRichText} from '@prismicio/vue';
    import {MuiTag} from '#components';

    type DuoColumnTheme = 'default' | 'primary' | 'secondary';

    type DuoImageItem = {
        image: ImageField;
    };

    interface Props {
        tag: KeyTextField;
        title: RichTextField;
        description: RichTextField;
        theme: DuoColumnTheme;
        images: DuoImageItem[];
        index: number;
        align?: 'start' | 'end';
    }

    const {tag, title, description, theme, images, index, align = 'start'} = defineProps<Props>();

    const visibleImages = computed(() => images.filter((item) => isFilled.image(item.image)).slice(0, 5));
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-content-columns-duo-column {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--joy-core-spacing-4);

        @include mq.screen_md(min) {
            width: 350px;
        }

        @include mq.screen_lg(min) {
            width: 450px;
        }

        &--align-end {
            @include mq.screen_md(min) {
                align-items: flex-end;
                text-align: right;

                .slice-content-columns-duo-column__title,
                .slice-content-columns-duo-column__description {
                    text-align: right;
                }

                .slice-content-columns-duo-column__image:nth-child(1) {
                    top: 0;
                    right: 0;
                }

                .slice-content-columns-duo-column__image:nth-child(2) {
                    bottom: 0;
                    left: 0;
                }

                .slice-content-columns-duo-column__image:nth-child(3) {
                    top: var(--joy-core-spacing-2);
                    left: var(--joy-core-spacing-2);
                }

                .slice-content-columns-duo-column__image:nth-child(4) {
                    bottom: var(--joy-core-spacing-2);
                    right: var(--joy-core-spacing-2);
                }

                .slice-content-columns-duo-column__image:nth-child(5) {
                    bottom: 33%;
                    left: 33%;
                }
            }
        }

        &__title {
            margin: 0;
            color: var(--joy-color-neutral-60);

            @include text.getFontBySize(primary-650);

            h2,
            h3 {
                margin: 0;
                font: inherit;
                color: inherit;
            }

            @include mq.screen_md(min) {
                @include text.getFontBySize(primary-850);
            }
        }

        &__description {
            margin: 0;
            color: var(--joy-color-neutral-60);

            @include text.getFontBySize(primary-400);
        }

        &__duo {
            position: relative;
            width: 100%;
            min-height: 450px;
            margin-top: var(--joy-core-spacing-4);
            overflow: hidden;
            padding: var(--joy-core-spacing-6) var(--joy-core-spacing-4);
            background-image: radial-gradient(
                circle at 50% 50%,
                var(--joy-color-primary-30) 0%,
                var(--joy-color-primary-10) 33%,
                var(--joy-color-neutral-0) 66%
            );

            @include mq.screen_md(min) {
                padding: var(--joy-core-spacing-8) var(--joy-core-spacing-6);
            }
        }

        &[data-mui-theme='secondary'] &__duo {
            background-image: radial-gradient(
                circle at 50% 50%,
                var(--joy-color-brand-primary-30) 0%,
                var(--joy-color-brand-primary-10) 33%,
                var(--joy-color-neutral-0) 66%
            );
        }

        &__image {
            position: relative;
            max-width: 45%;
            max-height: 60%;
            width: auto;
            height: auto;
            object-fit: contain;
            border-radius: var(--joy-core-radius-3);
            box-shadow: var(--joy-core-elevation-2);
            background-color: var(--joy-color-neutral-0);

            @include mq.screen_md(min) {
                position: absolute;
            }
        }

        &--align-start {
            @include mq.screen_md(min) {
                .slice-content-columns-duo-column__image:nth-child(1) {
                    top: 0;
                    left: 0;
                }

                .slice-content-columns-duo-column__image:nth-child(2) {
                    bottom: 0;
                    right: 0;
                }

                .slice-content-columns-duo-column__image:nth-child(3) {
                    top: var(--joy-core-spacing-2);
                    right: var(--joy-core-spacing-2);
                }

                .slice-content-columns-duo-column__image:nth-child(4) {
                    bottom: var(--joy-core-spacing-2);
                    left: var(--joy-core-spacing-2);
                }

                .slice-content-columns-duo-column__image:nth-child(5) {
                    bottom: 33%;
                    right: 33%;
                }
            }
        }
    }
</style>
